/**
 * Eén plek voor het praten met de server.
 *
 * De drie schermen hadden elk een eigen fetchData, en daarnaast bouwden 29
 * aanroepen fetch met de hand op, met de Authorization-header elke keer
 * opnieuw getypt. Bij een deel daarvan werd niet gekeken of de server ja
 * zei, zodat een mislukking stil bleef.
 *
 * Nu doet `verzoek` dat één keer: het adres, het token uit de browser, JSON
 * heen en terug, en een fout met de melding van de server als die er een
 * geeft. Een 401 betekent dat het token is verlopen (na 24 uur) of
 * ongeldig is; dan is niets op het scherm meer te vertrouwen, en gaat de
 * gebruiker terug naar het inlogscherm.
 *
 * De schermen gebruiken dit niet rechtstreeks maar via api/leerling.js,
 * api/leerkracht.js en api/beheer.js: één functie per endpoint.
 */

// Leeg: dezelfde oorsprong als de pagina. nginx stuurt /auth, /admin,
// /teacher, /student en /prompts door naar de backend. Voor een backend
// elders zet je REACT_APP_API_URL bij het bouwen.
const API_URL = process.env.REACT_APP_API_URL || '';

let bijVerlopenToken = () => {};

/** App vertelt hier wat er moet gebeuren als het token niet meer geldt. */
export function zetBijVerlopenToken(fn) {
  bijVerlopenToken = fn;
}

/**
 * Een verzoek aan de server.
 *
 *   verzoek('/teacher/classes')                          GET, geeft de JSON terug
 *   verzoek('/teacher/classes', { method: 'POST', json: { naam } })
 *   verzoek('/student/read-aloud', { method: 'POST', json, als: 'blob' })
 *   verzoek('/admin/texts/generate', { method: 'POST', json, wachtMs: 180000 })
 *
 * Gooit een Error met de melding van de server (`detail`) als het antwoord
 * geen succes is. `wachtMs` breekt het verzoek af na zoveel milliseconden;
 * de fout heet dan 'AbortError', zodat het scherm "duurde te lang" kan zeggen.
 */
export async function verzoek(pad, { method = 'GET', json, body, headers = {}, als = 'json', wachtMs } = {}) {
  const token = window.localStorage.getItem('token');
  const kop = { ...headers };
  if (token) kop.Authorization = `Bearer ${token}`;
  let inhoud = body;
  if (json !== undefined) {
    kop['Content-Type'] = 'application/json';
    inhoud = JSON.stringify(json);
  }

  let afbreker;
  let wekker;
  if (wachtMs) {
    afbreker = new AbortController();
    wekker = setTimeout(() => afbreker.abort(), wachtMs);
  }
  try {
    const antwoord = await fetch(API_URL + pad, {
      method, headers: kop, body: inhoud, signal: afbreker ? afbreker.signal : undefined,
    });
    if (antwoord.status === 401 && token) {
      bijVerlopenToken();
    }
    if (!antwoord.ok) {
      const fout = await antwoord.json().catch(() => ({}));
      const e = new Error(foutmelding(fout.detail, antwoord.status));
      e.status = antwoord.status;  // zo ziet een scherm het verschil met "geen verbinding"
      throw e;
    }
    if (als === 'blob') return antwoord.blob();
    return antwoord.json();
  } finally {
    if (wekker) clearTimeout(wekker);
  }
}

// FastAPI geeft bij een validatiefout een lijst in `detail`; die is voor een
// leerkracht onleesbaar.
function foutmelding(detail, status) {
  if (typeof detail === 'string' && detail) return detail;
  return `Er ging iets mis bij de server (${status}). Probeer het opnieuw.`;
}

/** Inloggen: het formulier gaat als formuliergegevens, zoals OAuth2 dat wil. */
export function inloggen(gebruikersnaam, wachtwoord) {
  const body = new URLSearchParams();
  body.append('username', gebruikersnaam);
  body.append('password', wachtwoord);
  return verzoek('/auth/login', {
    method: 'POST', body, headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
}
